import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../../contexts/data-context/use-data';
import { useUser } from '../../../../contexts/user-context/use-user';
import { CategoryView, TransactionView, VendorView } from '../../../../types/entity-types';
import { getSwrFetcherWithDynamicUrl } from '../../../../utils/get-swr-fetcher-with-dynamic-url';
import { changeVendor } from '../../utils/change-vendor';
import { editVendor, editVendorAndMergeTransactions } from '../../utils/edit-vendor';
import { ChangeVendorModal } from '../change-vendor-modal';
import { EditVendorModal } from '../edit-vendor-modal/edit-vendor-modal';
import { EvidenceViewModal } from './evidence/evidence-view-modal/evidence-view-modal';
import { TransactionDetailsDrawer } from './transaction-details-drawer';

type VendorEditParams = {
  name: string;
  categoryId: string;
  description?: string;
};

type EditResult = {
  success: boolean;
  updatedVendor?: VendorView;
  isDuplicate?: boolean;
  duplicateVendorIds?: string[];
  incrementalTransactionIds?: string[];
};

type TransactionDetailsDrawerWrapperProps = {
  selectedTransaction: TransactionView | null;
  transactions: TransactionView[];
  categories: CategoryView[];
  isNavVisible?: boolean;
  onCloseDrawer: () => void;
  onChangeSelectedTransaction: (id: string) => void;
};

export const TransactionDetailsDrawerWrapper = ({
  selectedTransaction,
  transactions,
  categories,
  isNavVisible = true,
  onCloseDrawer,
  onChangeSelectedTransaction,
}: TransactionDetailsDrawerWrapperProps) => {
  const analytics = useAnalytics();

  const { trigger: getDocumentSignedUrl } = useSWRMutation<{ url: string }, AxiosError, string, { urlSuffix: string }>(
    `/api/documents`,
    getSwrFetcherWithDynamicUrl,
  );
  const [isChangeVendorModalOpen, setIsChangeVendorModalOpen] = useState(false);
  const [isEditVendorModalOpen, setIsEditVendorModalOpen] = useState(false);
  const [signedDocumentUrl, setSignedDocumentUrl] = useState<string | null>(null);
  const [isEvidenceViewModalOpen, setIsEvidenceViewModalOpen] = useState(false);
  const [evidenceFileName, setEvidenceFileName] = useState<string | null>(null);
  const [duplicateVendorInfo, setDuplicateVendorInfo] = useState<{
    isDuplicate: boolean;
    duplicateVendorIds?: string[];
    incrementalTransactionIds?: string[];
  }>({ isDuplicate: false });
  const [isEditingVendor, setIsEditingVendor] = useState(false);

  const { data, mutations } = useData();
  const { authenticatedFetch } = useUser();

  const cachedOnChangeSelectedTransaction = useCallback(onChangeSelectedTransaction, [onChangeSelectedTransaction]);

  useEffect(() => {
    const handleTransactionNavigate = (event: CustomEvent) => {
      const { id } = event.detail;
      cachedOnChangeSelectedTransaction(id);
    };
    window.addEventListener('transactionNavigate', handleTransactionNavigate as EventListener);
    return () => window.removeEventListener('transactionNavigate', handleTransactionNavigate as EventListener);
  }, [cachedOnChangeSelectedTransaction, transactions]);

  const handleDuplicateVendor = (result: EditResult) => {
    setDuplicateVendorInfo({
      isDuplicate: true,
      duplicateVendorIds: result.duplicateVendorIds,
      incrementalTransactionIds: result.incrementalTransactionIds,
    });
  };

  const handleVendorEdit = async (
    editParams: VendorEditParams,
    options: {
      shouldMerge?: boolean;
      shouldCloseModal?: boolean;
      shouldOpenEditModal?: boolean;
    } = {},
  ): Promise<{ success: boolean; isDuplicate?: boolean }> => {
    if (!selectedTransaction?.vendor || !data) return { success: false };

    setIsEditingVendor(true);
    try {
      const baseArgs = {
        vendor: selectedTransaction.vendor,
        ...editParams,
        authenticatedFetch,
        data,
        mutations,
        vendors: data.vendors,
      };

      const result = options.shouldMerge
        ? await editVendorAndMergeTransactions({
            ...baseArgs,
            duplicateVendorIds: duplicateVendorInfo.duplicateVendorIds!,
            incrementalTransactionIds: duplicateVendorInfo.incrementalTransactionIds!,
          })
        : await editVendor(baseArgs);

      if (result.success && result.updatedVendor) {
        onChangeSelectedTransaction(selectedTransaction.id);

        if (options.shouldCloseModal) {
          onCloseEditVendorModal();
        }

        return { success: true };
      } else if (result.isDuplicate && !options.shouldMerge) {
        handleDuplicateVendor(result);

        if (options.shouldOpenEditModal) {
          setIsEditVendorModalOpen(true);
        }

        return { success: false, isDuplicate: true };
      }

      return { success: false };
    } finally {
      setIsEditingVendor(false);
    }
  };

  const onChangeVendor = async (vendorId: string) => {
    if (!selectedTransaction || !data) return;

    const newVendor = await changeVendor({
      transaction: selectedTransaction,
      vendorId,
      authenticatedFetch,
      data,
      mutations,
    });

    if (newVendor !== null) {
      setIsChangeVendorModalOpen(false);

      onChangeSelectedTransaction(selectedTransaction.id);
    }
  };

  const onCloseEditVendorModal = () => {
    setIsEditVendorModalOpen(false);
    setDuplicateVendorInfo({ isDuplicate: false });
  };

  const onEditVendor = async (editParams: VendorEditParams) => {
    return await handleVendorEdit(editParams, { shouldCloseModal: true });
  };

  const onEditVendorAndMerge = async (editParams: VendorEditParams) => {
    return await handleVendorEdit(editParams, { shouldMerge: true, shouldCloseModal: true });
  };

  const onChangeCategory = async (categoryId: string) => {
    if (!selectedTransaction?.vendor) return;

    const editParams: VendorEditParams = {
      name: selectedTransaction.vendor.name,
      categoryId,
      description: selectedTransaction.vendor.description || undefined,
    };

    try {
      const result = await handleVendorEdit(editParams, { shouldOpenEditModal: true });
      if (result.success) {
        toast.success('Vendor updated successfully');
      }

      const newCategory = categories.find((c) => c.id === categoryId);

      analytics.track('transaction_category_updated', {
        transaction_id: selectedTransaction?.id ?? '',
        old_category_id: selectedTransaction?.vendor?.category?.id ?? '',
        old_category_name: selectedTransaction?.vendor?.category?.level2 ?? '',
        new_category_id: categoryId,
        new_category_name: newCategory?.level2 ?? '',
        change_source: 'drawer',
      });
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to update vendor');
    }
  };

  const onCheckNewDetails = async (editParams: VendorEditParams) => {
    if (!duplicateVendorInfo.isDuplicate) {
      return await onEditVendor(editParams);
    }

    return await onEditVendorAndMerge(editParams);
  };

  const onHandleViewEvidence = async (documentId: string) => {
    const response = await getDocumentSignedUrl({ urlSuffix: `/${documentId}/signed-url` });
    if (response?.url) {
      setSignedDocumentUrl(response.url);

      const evidenceItem = selectedTransaction?.evidence?.find((evidence) => evidence.document?.id === documentId);
      const fileName = evidenceItem?.document?.fileName || 'Document';
      setEvidenceFileName(fileName);

      setIsEvidenceViewModalOpen(true);
    }
  };

  const onHandleDownloadEvidence = async (documentId: string) => {
    const response = await getDocumentSignedUrl({ urlSuffix: `/${documentId}/signed-url` });
    if (response?.url) {
      const evidenceItem = selectedTransaction?.evidence?.find((evidence) => evidence.document?.id === documentId);
      const fileName = evidenceItem?.document?.fileName || 'document';
      const link = document.createElement('a');
      link.href = response.url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.error('Failed to download document. Please try again later or contact support.');
    }
  };

  return (
    <>
      <TransactionDetailsDrawer
        isOpen={!!selectedTransaction}
        onClose={onCloseDrawer}
        transaction={selectedTransaction}
        transactions={transactions}
        categories={categories}
        onChangeVendor={() => setIsChangeVendorModalOpen(true)}
        isEditingVendorModalOpen={isEditVendorModalOpen}
        isNavVisible={isNavVisible}
        onEditVendor={() => setIsEditVendorModalOpen(true)}
        onChangeCategory={onChangeCategory}
        onViewEvidence={(documentId) => onHandleViewEvidence(documentId)}
        onDownloadEvidence={(documentId) => onHandleDownloadEvidence(documentId)}
      />

      <ChangeVendorModal
        isOpen={isChangeVendorModalOpen}
        onClose={() => setIsChangeVendorModalOpen(false)}
        vendors={data?.vendors ?? []}
        onChangeVendor={onChangeVendor}
      />

      <EditVendorModal
        isOpen={isEditVendorModalOpen}
        onClose={onCloseEditVendorModal}
        name={selectedTransaction?.vendor?.name ?? ''}
        category={selectedTransaction?.vendor?.category}
        categories={categories}
        description={selectedTransaction?.vendor?.description}
        isDuplicateName={duplicateVendorInfo.isDuplicate}
        showCategorySelect={false}
        isLoading={isEditingVendor}
        onSubmit={async (values) => {
          if (!duplicateVendorInfo.isDuplicate) {
            await onCheckNewDetails(values);

            analytics.track('vendor_details_updated', {
              transaction_id: selectedTransaction?.id ?? '',
              old_vendor_id: selectedTransaction?.vendor?.id,
              old_vendor_name: selectedTransaction?.vendor?.name,
              new_vendor_name: values.name,
              change_source: 'drawer',
            });

            return;
          }
          await onEditVendorAndMerge(values);
        }}
      />

      {signedDocumentUrl && (
        <EvidenceViewModal
          isOpen={isEvidenceViewModalOpen}
          onClose={() => setIsEvidenceViewModalOpen(false)}
          signedUrl={signedDocumentUrl}
          fileName={evidenceFileName}
        />
      )}
    </>
  );
};
